<template>
  <MainContent>
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-12 col-lg-10">
          <CustomerImportHeader />
          <CustomerImportStepper :state="pageState" class="mb-5" />
          <CustomerImportForm v-if="showForm" />
          <CustomerImportPreflight
            v-if="showPreflight"
            :samples="samples"
            :count="preflightCount"
            :columns="preflightColumns"
            :rowCountMismatch="rowCountMismatch"
            :localRowCount="localRowCount"
          />
          <CustomerImportSummary
            v-if="status === 'SUMMARY'"
            :failedRows="failedRows"
            :errorCount="result.errorCount"
            :truncated="result.truncated"
            @done="finishSummary"
          />
          <CustomerImportDoneState v-if="status === 'DONE'" :count="result.importedCount" />
          <div
            v-if="isFailed"
            role="alert"
            class="alert alert-danger d-flex align-items-center justify-content-between"
            data-testid="customer-import-failed-alert"
          >
            <p class="m-0">We could not import your file. Please try again.</p>
            <button class="btn btn-white" data-testid="customer-import-retry" @click="reset">
              Try again
            </button>
          </div>
          <div
            v-if="timeoutText"
            role="alert"
            class="alert alert-warning d-flex align-items-center justify-content-between"
            data-testid="customer-import-timeout-alert"
          >
            <p class="m-0">{{ timeoutText }}</p>
            <router-link
              v-if="isImportTimeout"
              :to="{ name: 'customers' }"
              class="btn btn-white"
              data-testid="customer-import-back-to-customers"
            >
              Back to customers
            </router-link>
            <button v-else class="btn btn-white" data-testid="customer-import-retry" @click="reset">
              Try again
            </button>
          </div>
        </div>
      </div>
    </div>
  </MainContent>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import MainContent from '@/components/MainContent/MainContent'
import CustomerImportHeader from '@/components/Modules/Customer/CustomerImport/CustomerImportHeader'
import CustomerImportStepper from '@/components/Modules/Customer/CustomerImport/CustomerImportStepper'
import CustomerImportForm from '@/components/Modules/Customer/CustomerImport/CustomerImportForm'
import CustomerImportPreflight from '@/components/Modules/Customer/CustomerImport/CustomerImportPreflight'
import CustomerImportSummary from '@/components/Modules/Customer/CustomerImport/CustomerImportSummary'
import CustomerImportDoneState from '@/components/Modules/Customer/CustomerImport/CustomerImportDoneState'
import { trackEvent } from '@/lib/analytics'
import { STATUS } from '@/store/customer-import'

const userModule = createNamespacedHelpers('user')
const customerImportModule = createNamespacedHelpers('customerImport')

const PREFLIGHT_TIMEOUT_TEXT = 'We did not hear back about your file. Please try again.'
const IMPORT_TIMEOUT_TEXT =
  'Your import is still processing. Check the Customers list in a few minutes.'

export default {
  name: 'CustomersImportPage',
  components: {
    MainContent,
    CustomerImportHeader,
    CustomerImportStepper,
    CustomerImportForm,
    CustomerImportPreflight,
    CustomerImportSummary,
    CustomerImportDoneState
  },

  computed: {
    ...customerImportModule.mapState([
      'status',
      'preflight',
      'samples',
      'result',
      'failure',
      'rowCountMismatch',
      'localRowCount'
    ]),
    ...customerImportModule.mapGetters({
      pageState: 'selectPageState',
      failedRows: 'selectFailedRows'
    }),
    showForm() {
      return this.status === STATUS.IDLE || this.status === STATUS.AWAITING_PREFLIGHT
    },
    showPreflight() {
      return this.status === STATUS.PREFLIGHT || this.status === STATUS.IMPORTING
    },
    preflightCount() {
      return this.preflight ? this.preflight.count : 0
    },
    preflightColumns() {
      return this.preflight ? this.preflight.columns : []
    },
    isTimeoutFailure() {
      return this.status === STATUS.FAILED && !!this.failure && this.failure.reason === 'TIMEOUT'
    },
    isFailed() {
      return this.status === STATUS.FAILED && !this.isTimeoutFailure
    },
    isImportTimeout() {
      return this.status === STATUS.TIMED_OUT
    },
    timeoutText() {
      if (this.isTimeoutFailure) return PREFLIGHT_TIMEOUT_TEXT
      if (this.isImportTimeout) return IMPORT_TIMEOUT_TEXT
      return ''
    }
  },

  mounted() {
    trackEvent(this.$intercom, 'Customer Import')

    if (this.status !== STATUS.IMPORTING) this.reset()

    // TODO(OVA-5166): drop with the legacy websocket client.
    this.pushWSMessageHandler({
      key: 'customers-import',
      handler: (message) => this.receiveLegacyMessage(message)
    })
  },

  // The parsed rows are guest PII; only an import still in flight needs them after leaving.
  beforeDestroy() {
    if (this.status !== STATUS.IMPORTING) this.reset()
  },

  methods: {
    ...userModule.mapActions(['pushWSMessageHandler']),
    ...customerImportModule.mapActions(['reset', 'finishSummary', 'receiveLegacyMessage'])
  }
}
</script>

<style scoped lang="scss"></style>
